import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Coffee, Share2, Users, Mail, Building2, Handshake, ArrowRight, Phone, Send, Sparkles, MapPin, Calendar, Home } from 'lucide-react';
import AnimatedHeading from '../components/AnimatedHeading';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GetInvolvedPage = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [questionForm, setQuestionForm] = useState({ name: '', email: '', phone: '', message: '', language_pref: 'english' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Handle hash scrolling
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  // Share function
  const handleShare = async () => {
    const shareData = {
      title: 'The Safe House Children\'s Rescue Center',
      text: language === 'en' 
        ? 'Help rescue vulnerable children in Colombia. Every child deserves safety, love, and a future. Join the mission!'
        : '¡Ayuda a rescatar niños vulnerables en Colombia. Cada niño merece seguridad, amor y un futuro. ¡Únete a la misión!',
      url: window.location.origin
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.debug('Share cancelled or failed:', err.message);
      }
    } else {
      // Fallback: try to copy to clipboard
      try {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert(language === 'en' ? 'Link copied to clipboard!' : '¡Enlace copiado al portapapeles!');
      } catch (err) {
        // Clipboard failed, open share via email as fallback
        const subject = encodeURIComponent(shareData.title);
        const body = encodeURIComponent(`${shareData.text}\n\n${shareData.url}`);
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
      }
    }
  };

  const waysToHelp = [
    {
      icon: <Heart className="w-12 h-12" />,
      title: language === 'en' ? 'Give a Donation' : 'Haz una Donación',
      description: language === 'en' 
        ? 'Make a one-time gift or become a monthly partner to provide consistent care for children.'
        : 'Haz un regalo único o conviértete en socio mensual para proporcionar cuidado consistente a los niños.',
      cta: language === 'en' ? 'Donate Now' : 'Donar Ahora',
      link: '/donate',
      color: 'from-sh-primary to-sh-bright',
      testId: 'give-section'
    },
    // TEMPORARILY HIDDEN - Coffee card hidden per user request, restore when ready
    // {
    //   icon: <Coffee className="w-12 h-12" />,
    //   title: language === 'en' ? 'Buy Safe House Coffee' : 'Compra Safe House Coffee',
    //   description: language === 'en'
    //     ? '100% of coffee proceeds go directly to children. Every cup creates hope.'
    //     : '100% de las ganancias del café van directamente a los niños. Cada taza crea esperanza.',
    //   cta: language === 'en' ? 'Shop Coffee' : 'Comprar Café',
    //   link: '/coffee',
    //   color: 'from-amber-500 to-amber-600',
    //   testId: 'coffee-section'
    // },
    {
      icon: <Share2 className="w-12 h-12" />,
      title: language === 'en' ? 'Share Our Mission' : 'Comparte Nuestra Misión',
      description: language === 'en'
        ? 'Help us reach more people by sharing our story on social media and with your community.'
        : 'Ayúdanos a alcanzar más personas compartiendo nuestra historia en redes sociales y con tu comunidad.',
      cta: language === 'en' ? 'Share Now' : 'Compartir Ahora',
      onClick: handleShare,
      color: 'from-sh-mid to-sh-primary',
      testId: 'share-section'
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: language === 'en' ? 'Partner with Us' : 'Asóciate con Nosotros',
      description: language === 'en'
        ? 'Churches, businesses, and organizations can create lasting impact through partnership.'
        : 'Iglesias, empresas y organizaciones pueden crear impacto duradero a través de alianzas.',
      cta: language === 'en' ? 'Learn More' : 'Saber Más',
      link: '#corporate-partnership',
      color: 'from-sh-bright to-sh-primary',
      testId: 'partner-section'
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="get-involved-page">
      {/* Hero Section - Enhanced with more visual impact */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://www.vmissions.org/wp-content/uploads/IMG_1687-1200x682.jpg"
            alt="Children at The Safe House"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sh-dark/95 via-sh-dark/85 to-sh-dark/70"></div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-colombia-yellow/20 rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-sh-primary/20 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-colombia-yellow/20 backdrop-blur-sm border border-colombia-yellow/40 rounded-full mb-6">
                <Sparkles className="w-5 h-5 text-colombia-yellow" />
                <span className="text-colombia-yellow text-sm font-bold tracking-wider uppercase">
                  {language === 'en' ? 'Be Part of Something Bigger' : 'Sé Parte de Algo Más Grande'}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
                {language === 'en' ? 'Your Heart Can' : 'Tu Corazón Puede'}
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-colombia-yellow via-amber-400 to-colombia-yellow">
                  {language === 'en' ? 'Save a Life' : 'Salvar una Vida'}
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 max-w-2xl mb-8 leading-relaxed">
                {language === 'en'
                  ? "A child is waiting for someone like you. Whether you give, share, pray, or show up — your heart can be the reason a child is rescued, fed, and loved today."
                  : "Un niño está esperando a alguien como tú. Ya sea que des, compartas, ores o te presentes — tu corazón puede ser la razón por la que un niño sea rescatado, alimentado y amado hoy."}
              </p>
              <p className="text-lg text-colombia-yellow font-semibold italic">
                {language === 'en'
                  ? '"There is no small act when a life hangs in the balance."'
                  : '"No hay acto pequeño cuando una vida está en juego."'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bible Verse - James 1:27 */}
      <section className="pt-16 pb-8 bg-gradient-to-r from-amber-50 via-white to-amber-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="relative inline-block">
              <div className="absolute -top-4 -left-6 text-6xl text-colombia-yellow/30 font-serif">"</div>
              <p className="text-2xl md:text-3xl text-gray-800 font-medium italic leading-relaxed px-8">
                {language === 'en' 
                  ? 'What God accepts as pure is this: caring for orphans & widows who need help'
                  : 'La religión pura y sin mancha delante de Dios es esta: cuidar de los huérfanos y las viudas que necesitan ayuda'}
              </p>
              <div className="absolute -bottom-4 -right-6 text-6xl text-colombia-yellow/30 font-serif">"</div>
            </div>
            <p className="mt-6 text-lg font-bold text-sh-primary">
              {language === 'en' ? 'James 1:27' : 'Santiago 1:27'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Ways to Help */}
      <section className="pt-10 pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {waysToHelp.map((way, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-3xl p-8 hover:shadow-xl transition-all duration-300"
                data-testid={way.testId}
              >
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${way.color} flex items-center justify-center text-white mb-6`}>
                  {way.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{way.title}</h3>
                <p className="text-gray-600 mb-6">{way.description}</p>
                {way.onClick ? (
                  <button
                    onClick={way.onClick}
                    className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${way.color} text-white rounded-full font-semibold hover:shadow-lg transition-all`}
                    data-testid={`${way.testId}-btn`}
                  >
                    {way.cta}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                ) : way.link.startsWith('#') ? (
                  <a
                    href={way.link}
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById(way.link.slice(1));
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${way.color} text-white rounded-full font-semibold hover:shadow-lg transition-all`}
                    data-testid={`${way.testId}-btn`}
                  >
                    {way.cta}
                    <ArrowRight className="w-5 h-5" />
                  </a>
                ) : (
                  <Link
                    to={way.link}
                    className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${way.color} text-white rounded-full font-semibold hover:shadow-lg transition-all`}
                    data-testid={`${way.testId}-btn`}
                  >
                    {way.cta}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bible Verse - Deuteronomy 10:18 */}
      <section className="py-16 bg-gradient-to-br from-sh-dark/5 via-white to-sh-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="relative inline-block">
              <div className="absolute -top-4 -left-6 text-6xl text-sh-primary/20 font-serif">"</div>
              <p className="text-2xl md:text-3xl text-gray-800 font-medium italic leading-relaxed px-8">
                {language === 'en' 
                  ? 'He executes justice for the orphan and widow, and shows His love to the foreigner by giving him food and clothing.'
                  : 'Él hace justicia al huérfano y a la viuda, y muestra Su amor al extranjero dándole comida y ropa.'}
              </p>
              <div className="absolute -bottom-4 -right-6 text-6xl text-sh-primary/20 font-serif">"</div>
            </div>
            <p className="mt-6 text-lg font-bold text-sh-primary">
              {language === 'en' ? 'Deuteronomy 10:18' : 'Deuteronomio 10:18'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Volunteer & Mission Trips Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-gradient-to-br from-sh-primary/20 to-colombia-yellow/20 rounded-3xl transform -rotate-3"></div>
                <img 
                  src={`${API}/api/uploads/mission_trip.jpg`}
                  alt="Mission Trip"
                  className="relative rounded-3xl shadow-2xl w-full h-80 object-cover"
                />
                <div className="absolute -bottom-4 -right-4 bg-colombia-yellow text-gray-900 px-6 py-3 rounded-2xl font-bold shadow-lg">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    {language === 'en' ? 'Year-Round Trips' : 'Viajes Todo el Año'}
                  </div>
                </div>
              </motion.div>

              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-sh-primary/10 rounded-full mb-4">
                  <Users className="w-5 h-5 text-sh-primary" />
                  <span className="text-sh-primary font-semibold text-sm uppercase tracking-wider">
                    {language === 'en' ? 'Experience the Mission' : 'Experimenta la Misión'}
                  </span>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  {language === 'en' ? 'Volunteer Opportunities & Mission Trips' : 'Oportunidades de Voluntariado y Viajes Misioneros'}
                </h2>
                <p className="text-xl text-gray-600 mb-6">
                  {language === 'en'
                    ? 'We are coordinating mission trips throughout the year for teams to come visit and help the kids at our rescue center in Colombia. Experience the mission firsthand and make memories that will last a lifetime while changing lives forever.'
                    : 'Estamos coordinando viajes misioneros durante todo el año para que equipos vengan a visitar y ayudar a los niños en nuestro centro de rescate en Colombia. Experimenta la misión de primera mano y crea recuerdos que durarán toda la vida mientras cambias vidas para siempre.'}
                </p>
                <ul className="space-y-3 mb-8">
                  {(language === 'en' ? [
                    'Build meaningful relationships by serving alongside our staff and spending time with the children',
                    'Lead engaging activities, games, and moments of worship that bring joy and encouragement',
                    'Take part in hands-on projects to improve and expand the Rescue Center',
                    'Use your unique gifts and talents to make a lasting impact in the lives of the children',
                    'Be part of outreach efforts, sharing hope and love with the surrounding community',
                    'Experience personal growth as you step out of your comfort zone and serve with purpose'
                  ] : [
                    'Construye relaciones significativas sirviendo junto a nuestro personal y pasando tiempo con los niños',
                    'Lidera actividades dinámicas, juegos y momentos de adoración que traigan alegría y ánimo',
                    'Participa en proyectos prácticos para mejorar y expandir el Centro de Rescate',
                    'Usa tus dones y talentos únicos para dejar un impacto duradero en la vida de los niños',
                    'Sé parte de los esfuerzos de alcance, compartiendo esperanza y amor con la comunidad',
                    'Experimenta crecimiento personal al salir de tu zona de confort y servir con propósito'
                  ]).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-700">
                      <div className="w-2 h-2 bg-sh-primary rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/heros"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-sh-primary text-white rounded-full font-bold text-lg hover:shadow-xl transition-all"
                >
                  <ArrowRight className="w-5 h-5" />
                  {language === 'en' ? 'See More Information' : 'Ver Más Información'}
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Partnership */}
      <section id="corporate-partnership" className="py-20 bg-gradient-to-br from-sh-dark to-sh-mid text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Handshake className="w-16 h-16 text-colombia-yellow mb-6" />
                <h2 className="text-4xl font-bold mb-6">
                  {language === 'en' ? 'Corporate & Church Partnerships' : 'Alianzas Corporativas y de Iglesias'}
                </h2>
                <p className="text-gray-300 text-lg mb-6">
                  {language === 'en'
                    ? "Partner with The Safe House to create meaningful impact. We offer customized partnership opportunities for businesses, churches, and organizations of all sizes."
                    : "Asóciate con The Safe House para crear impacto significativo. Ofrecemos oportunidades de alianza personalizadas para empresas, iglesias y organizaciones de todos los tamaños."}
                </p>
                <ul className="space-y-3 mb-8">
                  {(language === 'en' ? [
                    'Employee giving programs',
                    'Mission trip sponsorships',
                    'Host a home event to raise funds with friends',
                    'Event partnerships',
                    'Brand collaboration'
                  ] : [
                    'Programas de donación de empleados',
                    'Patrocinios de viajes misioneros',
                    'Organiza un evento en casa para recaudar fondos con amigos',
                    'Alianzas para eventos',
                    'Colaboración de marca'
                  ]).map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-colombia-yellow rounded-full"></div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="mailto:colombia@vmissions.org"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-colombia-yellow text-gray-900 rounded-full font-bold hover:shadow-xl transition-all"
                >
                  {language === 'en' ? 'Become a Partner' : 'Conviértete en Socio'}
                  <ArrowRight className="w-5 h-5" />
                </a>
              </div>
              <div className="hidden md:block">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-colombia-yellow/30 to-transparent rounded-3xl transform rotate-6"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1630068846062-3ffe78aa5049?w=600" 
                    alt="Partnership - Team hands together" 
                    className="relative rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Have Questions - Contact Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-2 text-center">
            {language === 'en' ? 'Have Questions?' : '¿Tienes Preguntas?'}
          </h2>
          <p className="text-xl text-gray-700 font-semibold text-center mb-1">
            {language === 'en' ? 'Want more information?' : '¿Quieres más información?'}
          </p>
          <p className="text-lg text-gray-500 text-center mb-8">
            {language === 'en' ? "We'd love to hear from you!" : '¡Nos encantaría saber de ti!'}
          </p>

          {/* Phone Numbers */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a href="tel:+18137899362" className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 rounded-full font-semibold hover:bg-gray-200 transition-all">
              <Phone className="w-5 h-5 text-sh-primary" />
              +1 (813) 789-9362
            </a>
          </div>

          {/* Contact Form */}
          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {language === 'en' ? 'Thank You!' : '¡Gracias!'}
                </h3>
                <p className="text-gray-600">
                  {language === 'en' ? 'Your question has been received. We will get back to you soon.' : 'Tu pregunta ha sido recibida. Nos pondremos en contacto pronto.'}
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  setSubmitting(true);
                  try {
                    const res = await fetch(`${API}/api/questions`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify(questionForm)
                    });
                    if (res.ok) setSubmitted(true);
                    else alert(language === 'en' ? 'Failed to submit. Please try again.' : 'Error al enviar. Por favor intenta de nuevo.');
                  } catch (err) {
                    alert(language === 'en' ? 'Failed to submit. Please try again.' : 'Error al enviar. Por favor intenta de nuevo.');
                  }
                  setSubmitting(false);
                }}
                className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200 space-y-5"
                data-testid="question-form"
              >
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {language === 'en' ? 'Your Name' : 'Tu Nombre'}
                  </label>
                  <input
                    type="text"
                    value={questionForm.name}
                    onChange={(e) => setQuestionForm({ ...questionForm, name: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-sh-primary focus:border-sh-primary transition-all"
                    placeholder={language === 'en' ? 'Your name' : 'Tu nombre'}
                    data-testid="question-name-input"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {language === 'en' ? 'Email' : 'Correo Electrónico'} *
                    </label>
                    <input
                      type="email"
                      required
                      value={questionForm.email}
                      onChange={(e) => setQuestionForm({ ...questionForm, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-sh-primary focus:border-sh-primary transition-all"
                      placeholder="you@example.com"
                      data-testid="question-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {language === 'en' ? 'Phone Number' : 'Número de Teléfono'}
                    </label>
                    <input
                      type="tel"
                      value={questionForm.phone}
                      onChange={(e) => setQuestionForm({ ...questionForm, phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-sh-primary focus:border-sh-primary transition-all"
                      placeholder="+1 (555) 000-0000"
                      data-testid="question-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {language === 'en' ? 'Your Question' : 'Tu Pregunta'} *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={questionForm.message}
                    onChange={(e) => setQuestionForm({ ...questionForm, message: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-sh-primary focus:border-sh-primary transition-all resize-none"
                    placeholder={language === 'en' ? 'Write your question here...' : 'Escribe tu pregunta aquí...'}
                    data-testid="question-message-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    {language === 'en' ? 'Preferred Language' : 'Idioma Preferido'}
                  </label>
                  <div className="flex gap-4">
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${questionForm.language_pref === 'english' ? 'border-sh-primary bg-sh-primary/5 text-sh-primary font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="language_pref"
                        value="english"
                        checked={questionForm.language_pref === 'english'}
                        onChange={(e) => setQuestionForm({ ...questionForm, language_pref: e.target.value })}
                        className="sr-only"
                      />
                      <span className="text-lg">🇺🇸</span> English
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 cursor-pointer transition-all ${questionForm.language_pref === 'spanish' ? 'border-sh-primary bg-sh-primary/5 text-sh-primary font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                      <input
                        type="radio"
                        name="language_pref"
                        value="spanish"
                        checked={questionForm.language_pref === 'spanish'}
                        onChange={(e) => setQuestionForm({ ...questionForm, language_pref: e.target.value })}
                        className="sr-only"
                      />
                      <span className="text-lg">🇨🇴</span> Español
                    </label>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-sh-primary text-white rounded-xl font-bold text-lg hover:bg-sh-dark transition-all flex items-center justify-center gap-2"
                  data-testid="question-submit-btn"
                >
                  {submitting ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {language === 'en' ? 'Submit' : 'Enviar'}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetInvolvedPage;
